import type { PageResult } from '@jiaofa/api-types'
import { request } from './request'

export interface AdminCategory {
  id: string
  storeId?: string
  storeIds?: string[]
  /** 与 storeIds 同序的门店名，查不到的门店为 null（后端占位，避免下标错位） */
  storeNames?: (string | null)[]
  name: string
  parentId?: string
  tab?: string
  iconUrl?: string
  sort?: number
  status: string
  supportsDelivery?: boolean
  createdAt?: string
}

export interface AdminProduct {
  id: string
  name: string
  categoryId: string
  categoryName?: string
  /** 商品门店范围：后端返回 JSON 文本（如 "[\"5\"]"），null 表示全部门店 */
  storeIds?: string | null
  /** 与 storeIds 对应的门店名，逗号分隔（如 "交发云咖"），null 表示全部门店 */
  storeNames?: string | null
  subtitle?: string
  mainImageUrl: string
  originalPrice?: number
  price: number
  status: string
  /** null 为平台公共商品，非空为门店私有商品。 */
  ownerStoreId?: string | null
  privateProduct?: boolean
  /** 当前门店上下架状态：ON / OFF / NOT_LISTED。 */
  listingStatus?: string | null
  storePrice?: number | null
  storeMemberPrice?: number | null
  supportsDelivery?: boolean
  /** 是否需要选规格（糖度/温度）：false 表示零食/包装类 */
  needSpec?: boolean
  stock?: number
  sales?: number
  createdAt?: string
  specGroups?: ProductSpecGroup[]
}

export interface ProductListParams {
  page?: number
  pageSize?: number
  keyword?: string
  categoryId?: string
  status?: string
  storeId?: string
}

export interface ProductSpecOption {
  id: string
  name: string
  priceInFen?: number
  enabled?: boolean
  sort?: number
  recommended?: boolean
}

export interface ProductSpecGroup {
  id: string
  name: string
  required: boolean
  multiple: boolean
  options: ProductSpecOption[]
}

/** 按规格提交库存（多规格商品用）：服务端按商品门店范围展开写入。 */
export interface SkuStockPayload {
  skuId: string
  stock: number
}

export interface ProductSavePayload {
  id?: string
  storeId?: string
  /** 后端单门店归属字段，使用 JSON 数组文本传输 */
  storeIds?: string
  ownerStoreId?: string
  categoryId: string
  name: string
  subtitle?: string
  description?: string
  mainImageUrl: string
  originalPrice?: number
  status?: string
  supportsDelivery?: boolean
  /** 是否需要选规格（糖度/温度）：关闭后小程序不弹选糖冰弹窗 */
  needSpec?: boolean
  stock?: number
  /** 传了 skuStocks 就不走单一 stock 口径（多规格商品平摊后提交） */
  skuStocks?: SkuStockPayload[]
  specGroups?: ProductSpecGroup[]
}

export interface CategorySavePayload {
  id?: string
  storeId?: string
  storeIds?: string[]
  name: string
  parentId?: string
  tab?: string
  iconUrl?: string
  sort?: number
  status?: string
  supportsDelivery?: boolean
}

export interface AdminSku {
  id: string
  productId: string
  productName?: string
  skuCode?: string
  specText?: string
  price?: number
  memberPrice?: number
  enabled?: boolean
  stock?: number
  ownerStoreId?: string | null
  privateSku?: boolean
  storePrice?: number | null
  storeMemberPrice?: number | null
}

export interface SkuSavePayload {
  id?: string
  productId: string
  skuCode?: string
  specText?: string
  price?: number
  memberPrice?: number
  enabled?: boolean
  ownerStoreId?: string
}

export interface ProductListingSku {
  skuId: string
  skuCode?: string
  specText?: string
  basePrice?: number
  baseMemberPrice?: number
  price?: number
  memberPrice?: number
  enabled?: boolean
  stock?: number
  safetyStock?: number
}

export interface ProductListing {
  productId: string
  productName: string
  storeId: string
  status: 'ON' | 'OFF' | 'NOT_LISTED' | string
  sort?: number
  skus: ProductListingSku[]
}

export interface ProductListingSavePayload {
  storeId: string
  status?: 'ON' | 'OFF'
  sort?: number
  skuPrices?: Array<{
    skuId: string
    price?: number
    memberPrice?: number
    enabled?: boolean
  }>
  skuStocks?: Array<{
    skuId: string
    stock: number
    safetyStock?: number
  }>
}

export interface AdminExtra {
  id: string
  storeId?: string
  name: string
  price?: number
  recommended?: boolean
  status: string
  sort?: number
}

export interface ExtraSavePayload {
  id?: string
  storeId?: string
  name: string
  price?: number
  recommended?: boolean
  enabled?: boolean
  sort?: number
}

export interface AdminInventory {
  skuId: string
  productId: string
  productName?: string
  storeId?: string
  stock: number
  safetyStock?: number
}

export interface InventoryListParams {
  page?: number
  pageSize?: number
}

export interface InventoryUpdatePayload {
  storeId?: string
  skuId: string
  stock: number
}

export interface AdminComboItem {
  productId: string
  productName?: string
  quantity?: number
}

export interface AdminCombo {
  id: string
  storeId?: string
  name: string
  originalPrice?: number
  salePrice?: number
  status?: string
  items?: AdminComboItem[]
  createdAt?: string
}

export interface ComboSavePayload {
  id?: string
  storeId?: string
  name: string
  originalPrice?: number
  salePrice?: number
  status?: string
  items?: AdminComboItem[]
}

export const productApiReady = true as const

export const fetchProducts = async (params: ProductListParams): Promise<PageResult<AdminProduct>> =>
  request<PageResult<AdminProduct>>({
    url: '/products',
    method: 'GET',
    params,
  })

export const createProduct = async (payload: ProductSavePayload): Promise<void> =>
  request<void, ProductSavePayload>({
    url: '/products',
    method: 'POST',
    data: payload,
  })

export const updateProduct = async (payload: ProductSavePayload): Promise<void> =>
  request<void, ProductSavePayload>({
    url: `/products/${payload.id}`,
    method: 'PUT',
    data: payload,
  })

export const deleteProduct = async (id: string): Promise<void> =>
  request<void>({
    url: `/products/${id}`,
    method: 'DELETE',
  })

export const fetchCategories = async (): Promise<AdminCategory[]> =>
  request<AdminCategory[]>({
    url: '/categories',
    method: 'GET',
  })

export const createCategory = async (payload: CategorySavePayload): Promise<void> =>
  request<void, CategorySavePayload>({
    url: '/categories',
    method: 'POST',
    data: payload,
  })

export const updateCategory = async (id: string, payload: CategorySavePayload): Promise<void> =>
  request<void, CategorySavePayload>({
    url: `/categories/${id}`,
    method: 'PUT',
    data: payload,
  })

export const deleteCategory = async (id: string): Promise<void> =>
  request<void>({
    url: `/categories/${id}`,
    method: 'DELETE',
  })

export const fetchSkus = async (productId?: string, storeId?: string): Promise<AdminSku[]> =>
  request<AdminSku[]>({
    url: '/skus',
    method: 'GET',
    params: productId || storeId ? { productId, storeId } : undefined,
  })

export const createSku = async (payload: SkuSavePayload): Promise<void> =>
  request<void, SkuSavePayload>({
    url: '/skus',
    method: 'POST',
    data: payload,
  })

export const updateSku = async (id: string, payload: SkuSavePayload): Promise<void> =>
  request<void, SkuSavePayload>({
    url: `/skus/${id}`,
    method: 'PUT',
    data: payload,
  })

export const deleteSku = async (id: string): Promise<void> =>
  request<void>({
    url: `/skus/${id}`,
    method: 'DELETE',
  })

export const fetchExtras = async (): Promise<AdminExtra[]> =>
  request<AdminExtra[]>({
    url: '/extras',
    method: 'GET',
  })

export const createExtra = async (payload: ExtraSavePayload): Promise<void> =>
  request<void, ExtraSavePayload>({
    url: '/extras',
    method: 'POST',
    data: payload,
  })

export const updateExtra = async (id: string, payload: ExtraSavePayload): Promise<void> =>
  request<void, ExtraSavePayload>({
    url: `/extras/${id}`,
    method: 'PUT',
    data: payload,
  })

export const deleteExtra = async (id: string): Promise<void> =>
  request<void>({
    url: `/extras/${id}`,
    method: 'DELETE',
  })

export const fetchInventory = async (params: InventoryListParams): Promise<PageResult<AdminInventory>> =>
  request<PageResult<AdminInventory>>({
    url: '/inventory',
    method: 'GET',
    params,
  })

export const updateInventory = async (payload: InventoryUpdatePayload): Promise<AdminInventory> =>
  request<AdminInventory, InventoryUpdatePayload>({
    url: '/inventory',
    method: 'PUT',
    data: payload,
  })

export const fetchCombos = async (): Promise<AdminCombo[]> =>
  request<AdminCombo[]>({
    url: '/combos',
    method: 'GET',
  })

export const createCombo = async (payload: ComboSavePayload): Promise<void> =>
  request<void, ComboSavePayload>({
    url: '/combos',
    method: 'POST',
    data: payload,
  })

export const updateCombo = async (id: string, payload: ComboSavePayload): Promise<void> =>
  request<void, ComboSavePayload>({
    url: `/combos/${id}`,
    method: 'PUT',
    data: payload,
  })

export const deleteCombo = async (id: string): Promise<void> =>
  request<void>({
    url: `/combos/${id}`,
    method: 'DELETE',
  })

export const fetchProductListing = async (productId: string, storeId: string): Promise<ProductListing> =>
  request<ProductListing>({
    url: `/products/${productId}/listing`,
    method: 'GET',
    params: { storeId },
  })

export const saveProductListing = async (
  productId: string,
  payload: ProductListingSavePayload,
): Promise<void> =>
  request<void, ProductListingSavePayload>({
    url: `/products/${productId}/listing`,
    method: 'PUT',
    data: payload,
  })

export const removeProductListing = async (productId: string, storeId: string): Promise<void> =>
  request<void>({
    url: `/products/${productId}/listing`,
    method: 'DELETE',
    params: { storeId },
  })
